import {DropdownWithTags} from '@/components-v2/molecules/DropdownWithTags/DropdownWithTags'
import {Tab} from '@/components-v2/molecules/Tab/Tab'
import {TabsNavigation} from '@/components-v2/molecules/TabsNavigation.jsx/TabsNavigation'
import {useGetCategoryDropdown} from '@/hooks/promotion/useGetCategoryDropdown'
import {useGetProductAndBrandDropdown} from '@/hooks/promotion/useGetProductAndBrandDropdown'
import {useCategoryList} from '@/hooks/useCategoryList'
import {useSubCategoryList} from '@/hooks/useSubCategoryList'
import {BuyType} from '@/utils/constants/promotionConstants'

const MAX_SELECTIONS = 5

/**
 * Handles Category/Sub-category and Product/Brand tab+dropdown selection within the Promotion
 * Selection modal (v2). Replaces the separate `CategoryAndSubCategoryContent`/`ProductAndBrandContent`
 * v1 components - both rendered an identical tab+dropdown+tags UI and only differed in which
 * dropdown data-fetching hooks they called.
 */
const DropdownSelectionContent = ({
  type,
  activeTabIndex,
  selectedTags,
  tabs = [],
  onTabChange = () => {},
  setSelectedTags = () => {},
}) => {
  const isCategoryType = type === BuyType.CATEGORY
  const activeTab = tabs[activeTabIndex]

  const dropdownType = isCategoryType
    ? activeTab?.dropDownType ||
      (activeTab?.id === 'CATEGORY' ? 'categories' : activeTab?.id === 'SUBCATEGORY' ? 'sub_categories' : '')
    : activeTab?.dropDownType === 'brands'
      ? 'display_brands'
      : activeTab?.dropDownType

  const resolvedDropdownType = dropdownType === 'categories' ? 'display_categories' : dropdownType

  const {categoryListData, isLoading: isLoadingCategories} = useCategoryList(
    resolvedDropdownType,
    isCategoryType && dropdownType === 'categories'
  )
  const {subCategoryListData, isLoading: isLoadingSubCategories} = useSubCategoryList(
    isCategoryType && dropdownType === 'sub_categories'
  )
  const {data: genericCategoryData, isLoading: isLoadingGenericCategory} = useGetCategoryDropdown(
    isCategoryType && !['categories', 'sub_categories'].includes(dropdownType) ? dropdownType : ''
  )
  const {data: productBrandData, isLoading: isLoadingProductBrand} = useGetProductAndBrandDropdown(
    isCategoryType ? '' : resolvedDropdownType
  )

  let childList
  let isLoading
  if (!isCategoryType) {
    childList = productBrandData?.data?.map(item => ({id: item.id, displayText: item.en ?? item.value})) || []
    isLoading = isLoadingProductBrand
  } else if (dropdownType === 'categories') {
    childList = categoryListData.map(item => ({id: item.id, displayText: item.en}))
    isLoading = isLoadingCategories
  } else if (dropdownType === 'sub_categories') {
    childList = subCategoryListData.map(item => ({id: item.id, displayText: item.en}))
    isLoading = isLoadingSubCategories
  } else {
    childList = genericCategoryData?.data?.map(item => ({id: item?.id ?? item, displayText: item?.en ?? item})) || []
    isLoading = isLoadingGenericCategory
  }

  const selectedOptions = selectedTags.itemType === activeTab?.id ? selectedTags.values : []

  const handleSelectedOptionsChange = value => {
    setSelectedTags({itemType: activeTab?.id || '', values: value})
  }

  return (
    <div className="py-4">
      <TabsNavigation selectedIndex={activeTabIndex} onUpdate={onTabChange} type="line">
        {tabs.map((tab, index) => (
          <Tab key={tab.id} label={tab.tabLabel} index={index} />
        ))}
      </TabsNavigation>
      <DropdownWithTags
        childList={childList}
        onChange={handleSelectedOptionsChange}
        placeholder={selectedOptions.length > 0 ? 'Selected' : activeTab?.tabLabel}
        selectedOptions={selectedOptions}
        label={activeTab?.tabLabel}
        selection={isLoading ? 'loading' : 'multiple'}
        maxSelections={MAX_SELECTIONS}
        usePortal
      />
    </div>
  )
}

export default DropdownSelectionContent
